import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2, Lock, User } from 'lucide-react';

export default function AdminLogin() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        const res = await login(username.trim(), password);
        setBusy(false);
        if (res.ok) {
            navigate('/admin');
        } else {
            setError(res.error || 'Invalid credentials');
        }
    };

    return (
        <div
            className="min-h-screen bg-black grain relative overflow-hidden flex flex-col"
            data-testid="admin-login-page"
        >
            <div aria-hidden className="pointer-events-none absolute inset-0 -z-0">
                <div className="absolute -top-20 -left-20 h-[380px] w-[380px] rounded-full bg-un-berry/30 blur-[120px] animate-blob" />
                <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-un-pumpkin/25 blur-[120px] animate-blob" style={{ animationDelay: '4s' }} />
            </div>

            <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-16">
                <div className="w-full max-w-md">
                    <Link
                        to="/"
                        data-testid="login-back-home"
                        aria-label="UnCloned® Brand Awards — home"
                        className="hero-logo-mask block mx-auto mb-8"
                        style={{
                            width: '320px',
                            maxWidth: '100%',
                            WebkitMaskImage: "url('/uncloned-brand-awards-wordmark.webp')",
                            maskImage: "url('/uncloned-brand-awards-wordmark.webp')",
                        }}
                    />

                    <div className="rounded-3xl border border-white/15 bg-black/70 backdrop-blur-md p-8 md:p-10 text-center">
                        <div className="text-xs uppercase tracking-[0.28em] text-un-water font-semibold">Admin</div>
                        <h1 className="mt-2 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl">
                            Sign in
                        </h1>
                        <p className="mt-3 text-white/60 text-sm font-body">
                            Manage award recipients and certificates.
                        </p>

                        <form onSubmit={onSubmit} className="mt-8 space-y-4 text-left">
                            <label className="block">
                                <span className="block text-[11px] uppercase tracking-[0.22em] text-white/70 mb-2">Username</span>
                                <div className="relative">
                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={16} />
                                    <input
                                        data-testid="login-username-input"
                                        type="text"
                                        required
                                        autoComplete="username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className="w-full rounded-full bg-neutral-950 border border-white/15 pl-11 pr-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry"
                                    />
                                </div>
                            </label>
                            <label className="block">
                                <span className="block text-[11px] uppercase tracking-[0.22em] text-white/70 mb-2">Password</span>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={16} />
                                    <input
                                        data-testid="login-password-input"
                                        type="password"
                                        required
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full rounded-full bg-neutral-950 border border-white/15 pl-11 pr-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry"
                                    />
                                </div>
                            </label>

                            {error && (
                                <div
                                    data-testid="login-error"
                                    className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                                >
                                    {error}
                                </div>
                            )}

                            <div className="flex justify-center pt-2">
                                <button
                                    data-testid="login-submit-btn"
                                    type="submit"
                                    disabled={busy}
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-un-lime text-black text-sm font-bold uppercase tracking-wide px-8 py-3 hover:bg-white transition-colors disabled:opacity-60"
                                >
                                    {busy ? <Loader2 className="animate-spin" size={16} /> : null}
                                    {busy ? 'Signing in…' : 'Sign in to admin dashboard'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="mt-6 text-center text-xs text-white/40">
                        Public directory is available at{' '}
                        <Link to="/" className="text-white/70 underline underline-offset-2">the homepage</Link>.
                    </div>
                </div>
            </div>
        </div>
    );
}
